import {
  ImageUploader, VideoUploader, FileUploader,
  // @ts-ignore
} from '@xhs/uploader'
// @ts-ignore
import { IdentityUploader } from '@xhs/uploader/src/IdentityUploader'
import { FileWithPath } from 'file-selector'
import { uniqueId } from 'lodash'
import { reactive } from 'vue'

export type InputFile = (FileWithPath | DataTransferItem | File) & { size?: number }
export type FileErrorCode = 'file-invalid-type' |
'file-too-large' |
'file-too-small' |
'too-many-files' |
'not-match-ratio' |
'not-match-width-height' |
string

export interface BeerBaseFileItem {
  filename?: string
  error?: FileErrorCode
  id?: string
  path?: string
  url?: string
  downloadUrl?: string
  uploadPercent?: number
  file?:InputFile
  base64?: string
  width?: number
  height?: number
  persistentId?: string
}

export type UploaderType = 'image' | 'video' | 'file' | 'identity'

export type NumberRange = [number, number] | number[]
export interface BeerFileItem extends BeerBaseFileItem {
  id: string
  post?: ((option: any) => Promise<BeerFileItem>)
}

export interface CreateUploaderOptions {
  type?: UploaderType
  bucket: string
  bizKey: string
  file: InputFile
  keyPattern?: string
  qiniuParam?: string
  useAccelerate?: boolean
  contentType?: string
  watermark?: string
  onProgress?: (event: { loaded: number; total: number; percent: number }) => void
}

export type UploadResult = {
  url?: string
  cdnUrl?: string
  path?: string
  error?: string
  [key: string]: any
}

export const FILE_ID_PREFIX = 'uploadfile-'

export function guid(): string {
  return uniqueId(FILE_ID_PREFIX)
}

export function initFileList(
  fileList: BeerBaseFileItem[],
  defaultUrl?: string,
): BeerFileItem[] {
  return fileList.map(file => {
    let { id } = file
    if (!id) {
      id = guid()
    }
    return reactive({
      id,
      filename: file.filename || file.path || file.url,
      url: file.url || defaultUrl,
      uploadPercent: 100,
      ...file,
    })
  })
}

export async function uploadXhsFile(options: CreateUploaderOptions): Promise<UploadResult > {
  const {
    type,
    bucket,
    bizKey,
    file,
    onProgress,
    qiniuParam,
    watermark,
    ...spr
  } = options
  let UploadClass = null
  switch (type) {
    case 'image':
      UploadClass = ImageUploader
      break

    case 'video':
      UploadClass = VideoUploader
      break

    case 'identity':
      UploadClass = IdentityUploader
      break

    default:
      UploadClass = FileUploader
      break
  }

  function myOnProgress(event: {loaded: number; total: number}) {
    const { loaded, total } = event
    let percent = Math.round(((event.loaded / event.total) * 10000) / 100)
    if (percent > 99) {
      percent = 99
    }

    if (onProgress) {
      onProgress({ loaded, total, percent })
    }
  }
  const uploader = new UploadClass({ bucket, bizKey, ...spr })
  const result = await uploader.post({
    file,
    onProgress: myOnProgress,
    qiniuParam,
    watermark,
  }).catch((e: Error) => ({ error: e.message }))
  return result
}

export function uploadXhsImageFile(options: Omit<CreateUploaderOptions, 'type'>): Promise<UploadResult> {
  return uploadXhsFile({
    type: 'image',
    ...options,
  })
}

export function uploadXhsVideoFile(options: Omit<CreateUploaderOptions, 'type'>): Promise<UploadResult> {
  return uploadXhsFile({
    type: 'video',
    ...options,
  })
}

export function readImgToBase64(file: File): Promise<string> {
  return new Promise(resolve => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result as string)
  })
}

export async function getImgSize(imgBase64: string): Promise<{ width: number; height: number}> {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.onload = () => {
      resolve({
        width: image.width,
        height: image.height,
      })
    }
    image.onerror = () => {
      reject(new Error('图片格式错误'))
    }
    image.src = imgBase64
  })
}

function matchRange(n: number, min: number, max: number): string {
  if (n >= (min || 1) && n <= (max || Number.MAX_SAFE_INTEGER)) {
    return ''
  }
  return 'not-match-width-height'
}

export type CheckFn = () => Promise<InputFile>
export type ValidateParams = {
  type: UploaderType
  ratio?: number | number[]
  widthRange?: NumberRange
  heightRange?: NumberRange
  customCheck?: CheckFn | CheckFn[]
}

export function getFileValidator({
  type = 'file',
  ratio,
  widthRange,
  heightRange,
  customCheck,
}: ValidateParams) {
  return (file: InputFile): Promise<InputFile> => {
    let q = Promise.resolve(file)
    if (type === 'image' && (ratio || widthRange || heightRange)) {
      q = readImgToBase64(file as File)
        .then(getImgSize)
        .then(({ width, height }) => {
          if (ratio) {
            const limitRatio = Array.isArray(ratio) ? ratio : [ratio]
            const isMatch = limitRatio.some(r => r === width / height)
            if (!isMatch) {
              throw new Error('图片宽高比例不符合要求')
            }
          }
          if (widthRange) {
            const [minWidth, maxWidth] = widthRange
            const error = matchRange(width, minWidth, maxWidth)
            if (error) {
              throw new Error(`图片宽度不在 [${minWidth}, ${maxWidth}] 范围`)
            }
          }
          if (heightRange) {
            const [minHeight, maxHeight] = heightRange
            const error = matchRange(height, minHeight, maxHeight)
            if (error) {
              throw new Error(`图片高度不在 [${minHeight}, ${maxHeight}] 范围`)
            }
          }
          return file
        })
    }
    if (customCheck) {
      const checkList = Array.isArray(customCheck) ? customCheck : [customCheck]
      checkList.forEach(fn => {
        q = q.then(fn)
      })
    }
    return q
  }
}

type UploadFactoryParams = {
  type: UploaderType
  bucket: string
  bizKey: string
}
export function postXhsItemGenerator({
  type,
  bucket,
  bizKey,
}: UploadFactoryParams): (file: InputFile) => BeerFileItem {
  return (file: InputFile) => {
    const fileId = guid()
    const job = reactive<BeerFileItem>({
      id: fileId,
      filename: (file as File).name,
      uploadPercent: 0,
      file,
    })
    job.post = (postOption: any) => {
      job.uploadPercent = 0
      job.error = undefined
      return uploadXhsFile({
        type,
        bucket,
        bizKey,
        file,
        ...postOption,
        onProgress: ({ percent }) => {
          job.uploadPercent = percent
        },
      })
        .then(uploadResult => {
          if (uploadResult.error) {
            job.error = uploadResult.error
          }
          job.downloadUrl = uploadResult.downloadUrl
          // to be check
          job.url = type === 'file' ? job.downloadUrl : uploadResult.cdnUrl
          job.path = uploadResult.key
          job.uploadPercent = 100
          job.persistentId = uploadResult.persistentId
          job.post = undefined
          job.file = undefined
          return job
        })
    }
    return job
  }
}
